/**
 * HELP PLAYBOOKS - Assistant Contextuel Généralisé
 * =================================================
 * P0: Base de connaissances structurée pour l'assistant IA
 * 
 * Structure:
 * - Playbooks par plateforme et intention
 * - Routeur d'intention basé sur contexte
 * - Actions CTA pour UI interactif
 */

import { PLATFORM_IDS, PLATFORMS_CONFIG, getPlatformName } from './platformConstants';

// =============================================================================
// CTA ACTION TYPES
// =============================================================================
export const CTA_ACTIONS = {
  OPEN_PLATFORM_MODAL: 'open_platform_modal',
  GO_TO_STEP: 'go_to_step',
  RETRY_CONNECTION: 'retry_connection',
  OPEN_EXTERNAL_LINK: 'open_external_link',
  DISMISS: 'dismiss',
  CONTACT_SUPPORT: 'contact_support',
  COPY_TO_CLIPBOARD: 'copy_to_clipboard'
};

// =============================================================================
// INTENT TYPES
// =============================================================================
export const INTENT_TYPES = {
  HOW_TO_CONNECT: 'how_to_connect',
  TROUBLESHOOTING: 'troubleshooting',
  GETTING_STARTED: 'getting_started',
  NEXT_STEP: 'next_step',
  ERROR_HELP: 'error_help',
  FEATURE_EXPLANATION: 'feature_explanation'
};

// =============================================================================
// PLAYBOOKS: HOW TO CONNECT - ENRICHED WITH TECHNICAL DATA
// =============================================================================
const CONNECT_PLAYBOOKS = {
  instagram: {
    id: 'instagram_how_to_connect',
    platform: 'instagram',
    intent: INTENT_TYPES.HOW_TO_CONNECT,
    title: 'Connecter Instagram',
    summary: 'Reliez votre compte Instagram Business pour publier automatiquement.',
    steps: [
      {
        order: 1,
        title: 'Compte Business requis',
        description: 'Votre compte Instagram doit être un compte Business ou Créateur, pas personnel.',
        tip: 'Allez dans Paramètres Instagram → Compte → Passer en compte professionnel'
      },
      {
        order: 2,
        title: 'Page Facebook liée',
        description: 'Instagram Business doit être lié à une Page Facebook.',
        tip: 'Dans les paramètres Instagram, Section "Comptes liés" → Facebook'
      },
      {
        order: 3,
        title: 'Autoriser les permissions',
        description: 'Cliquez sur "Configurer" et autorisez l\'accès à votre compte.',
        tip: 'Acceptez toutes les permissions demandées pour la publication'
      }
    ],
    // P0 UX OVERHAUL: Technical data enrichment
    technical: {
      callbackUrl: '/auth/callback/instagram',
      scopes: ['instagram_basic', 'instagram_content_publish', 'pages_show_list', 'pages_read_engagement'],
      prerequisites: [
        'Compte Instagram Business ou Créateur',
        'Page Facebook liée et accès administrateur',
        'Application Meta Developer configurée'
      ],
      developerPortalUrl: 'https://developers.facebook.com/apps',
      docUrl: 'https://developers.facebook.com/docs/instagram-api/getting-started'
    },
    ctas: [
      {
        label: 'Configurer Instagram',
        action: CTA_ACTIONS.OPEN_PLATFORM_MODAL,
        payload: { platform: 'instagram' },
        variant: 'primary'
      },
      {
        label: 'Copier Callback URL',
        action: CTA_ACTIONS.COPY_TO_CLIPBOARD,
        payload: { text: '/auth/callback/instagram', label: 'Callback URL' },
        variant: 'secondary'
      },
      {
        label: 'Guide Meta Business',
        action: CTA_ACTIONS.OPEN_EXTERNAL_LINK,
        payload: { url: 'https://www.facebook.com/business/help/898752960195806' },
        variant: 'secondary'
      }
    ],
    troubleshooting: [
      {
        symptom: 'Erreur "compte non professionnel"',
        solution: 'Convertissez votre compte en Business dans les paramètres Instagram'
      },
      {
        symptom: 'Aucune page Facebook trouvée',
        solution: 'Liez votre compte Instagram à une Page Facebook dont vous êtes administrateur'
      }
    ],
    commonErrors: [
      {
        code: 'OAuthException 190',
        meaning: 'Token expiré ou révoqué',
        fix: 'Reconnectez votre compte Instagram'
      },
      {
        code: 'Erreur 10',
        meaning: 'Permissions insuffisantes',
        fix: 'Vérifiez les permissions dans Facebook Business'
      }
    ],
    // Difficulty information
    difficulty: {
      level: 6,
      label: 'Très complexe',
      whyThisDifficulty: [
        'Nécessite un compte Instagram Business ou Créateur',
        'Doit être lié à une Page Facebook',
        'Configuration app Meta Developer requise',
        'Vérification des permissions multiples'
      ]
    }
  },

  facebook: {
    id: 'facebook_how_to_connect',
    platform: 'facebook',
    intent: INTENT_TYPES.HOW_TO_CONNECT,
    title: 'Connecter Facebook',
    summary: 'Publiez automatiquement sur votre Page Facebook professionnelle.',
    steps: [
      {
        order: 1,
        title: 'Page requise',
        description: 'Vous devez avoir une Page Facebook (pas un profil personnel).',
        tip: 'Créez une Page depuis facebook.com/pages/create'
      },
      {
        order: 2,
        title: 'Rôle administrateur',
        description: 'Vous devez être administrateur de la Page.',
        tip: 'Vérifiez dans Paramètres de la Page → Rôles de la Page'
      },
      {
        order: 3,
        title: 'Autoriser la connexion',
        description: 'Cliquez sur "Configurer" et sélectionnez votre Page.',
        tip: 'Choisissez la Page sur laquelle vous souhaitez publier'
      }
    ],
    technical: {
      callbackUrl: '/auth/callback/facebook',
      scopes: ['pages_manage_posts', 'pages_read_engagement', 'pages_show_list'],
      prerequisites: [
        'Page Facebook professionnelle',
        'Accès administrateur à la Page'
      ],
      developerPortalUrl: 'https://developers.facebook.com/apps',
      docUrl: 'https://developers.facebook.com/docs/pages-api/getting-started'
    },
    ctas: [
      {
        label: 'Configurer Facebook',
        action: CTA_ACTIONS.OPEN_PLATFORM_MODAL,
        payload: { platform: 'facebook' },
        variant: 'primary'
      },
      {
        label: 'Copier Callback URL',
        action: CTA_ACTIONS.COPY_TO_CLIPBOARD,
        payload: { text: '/auth/callback/facebook', label: 'Callback URL' },
        variant: 'secondary'
      },
      {
        label: 'Créer une Page',
        action: CTA_ACTIONS.OPEN_EXTERNAL_LINK,
        payload: { url: 'https://www.facebook.com/pages/create' },
        variant: 'secondary'
      }
    ],
    troubleshooting: [
      {
        symptom: 'Aucune Page disponible',
        solution: 'Créez une Page Facebook ou vérifiez que vous en êtes administrateur'
      },
      {
        symptom: 'Erreur de permissions',
        solution: 'Reconnectez-vous et acceptez toutes les permissions demandées'
      }
    ],
    commonErrors: [
      {
        code: 'OAuthException 190',
        meaning: 'Token expiré',
        fix: 'Reconnectez votre compte'
      },
      {
        code: 'Erreur 200',
        meaning: 'Permissions Page insuffisantes',
        fix: 'Vérifiez que vous êtes admin de la Page'
      }
    ],
    // Difficulty information
    difficulty: {
      level: 5,
      label: 'Complexe',
      whyThisDifficulty: [
        'Nécessite une Page Facebook professionnelle',
        'Accès administrateur requis',
        'Configuration app Meta Developer'
      ]
    }
  },

  x: {
    id: 'x_how_to_connect',
    platform: 'x',
    intent: INTENT_TYPES.HOW_TO_CONNECT,
    title: 'Connecter X (Twitter)',
    summary: 'Publiez automatiquement vos tweets.',
    steps: [
      {
        order: 1,
        title: 'Compte X vérifié',
        description: 'Assurez-vous d\'avoir un compte X actif.',
        tip: 'Un email et téléphone vérifiés sont recommandés'
      },
      {
        order: 2,
        title: 'Autoriser l\'application',
        description: 'Cliquez sur "Configurer" et autorisez notre application.',
        tip: 'X vous redirigera vers une page d\'autorisation'
      },
      {
        order: 3,
        title: 'Confirmer l\'accès',
        description: 'Acceptez les permissions de publication.',
        tip: 'L\'accès peut être révoqué à tout moment dans les paramètres X'
      }
    ],
    technical: {
      callbackUrl: '/auth/callback/x',
      scopes: ['tweet.read', 'tweet.write', 'users.read', 'offline.access'],
      prerequisites: [
        'Compte X actif',
        'Email et téléphone vérifiés (recommandé)'
      ],
      developerPortalUrl: 'https://developer.twitter.com/en/portal/dashboard',
      docUrl: 'https://developer.twitter.com/en/docs/twitter-api'
    },
    ctas: [
      {
        label: 'Configurer X',
        action: CTA_ACTIONS.OPEN_PLATFORM_MODAL,
        payload: { platform: 'x' },
        variant: 'primary'
      },
      {
        label: 'Copier Callback URL',
        action: CTA_ACTIONS.COPY_TO_CLIPBOARD,
        payload: { text: '/auth/callback/x', label: 'Callback URL' },
        variant: 'secondary'
      }
    ],
    troubleshooting: [
      {
        symptom: 'Erreur de rate limit',
        solution: 'L\'API X Free a des limites. Attendez quelques minutes et réessayez.'
      },
      {
        symptom: 'Image noire dans le post',
        solution: 'Bug connu en cours de résolution. Le texte sera publié correctement.'
      }
    ],
    commonErrors: [
      {
        code: 'Rate Limit 429',
        meaning: 'Trop de requêtes (API Free)',
        fix: 'Attendez quelques minutes avant de réessayer'
      },
      {
        code: 'Forbidden 403',
        meaning: 'Accès refusé',
        fix: 'Vérifiez les permissions de votre app X'
      }
    ],
    // Difficulty information
    difficulty: {
      level: 4,
      label: 'Avancé',
      whyThisDifficulty: [
        'Création d\'app sur X Developer Portal requise',
        'Configuration OAuth 2.0',
        'Limites de l\'API Free (1500 tweets/mois)'
      ]
    }
  },

  linkedin: {
    id: 'linkedin_how_to_connect',
    platform: 'linkedin',
    intent: INTENT_TYPES.HOW_TO_CONNECT,
    title: 'Connecter LinkedIn',
    summary: 'Publiez du contenu professionnel sur LinkedIn.',
    steps: [
      {
        order: 1,
        title: 'Compte LinkedIn actif',
        description: 'Vous devez avoir un compte LinkedIn personnel ou une Page entreprise.',
        tip: 'Les Pages entreprise nécessitent des permissions administrateur'
      },
      {
        order: 2,
        title: 'Autoriser notre app',
        description: 'Cliquez sur "Configurer" et connectez-vous à LinkedIn.',
        tip: 'LinkedIn vous demandera de confirmer les permissions'
      },
      {
        order: 3,
        title: 'Choisir le profil',
        description: 'Sélectionnez si vous publiez sur votre profil ou une Page.',
        tip: 'Pour les Pages entreprise, vous devez en être administrateur'
      }
    ],
    technical: {
      callbackUrl: '/auth/callback/linkedin',
      scopes: ['r_liteprofile', 'w_member_social', 'r_organization_social'],
      prerequisites: [
        'Compte LinkedIn personnel ou Page entreprise',
        'Pour les Pages: accès administrateur'
      ],
      developerPortalUrl: 'https://www.linkedin.com/developers/apps',
      docUrl: 'https://learn.microsoft.com/en-us/linkedin/marketing/'
    },
    ctas: [
      {
        label: 'Configurer LinkedIn',
        action: CTA_ACTIONS.OPEN_PLATFORM_MODAL,
        payload: { platform: 'linkedin' },
        variant: 'primary'
      },
      {
        label: 'Copier Callback URL',
        action: CTA_ACTIONS.COPY_TO_CLIPBOARD,
        payload: { text: '/auth/callback/linkedin', label: 'Callback URL' },
        variant: 'secondary'
      }
    ],
    troubleshooting: [
      {
        symptom: 'Page entreprise non visible',
        solution: 'Vérifiez que vous êtes administrateur de la Page LinkedIn'
      }
    ],
    commonErrors: [
      {
        code: 'Unauthorized 401',
        meaning: 'Token expiré',
        fix: 'Reconnectez votre compte LinkedIn'
      }
    ],
    // Difficulty information
    difficulty: {
      level: 5,
      label: 'Complexe',
      whyThisDifficulty: [
        'Création d\'app LinkedIn Developer requise',
        'Configuration OAuth',
        'Accès admin pour les Pages entreprise'
      ]
    }
  },

  blog: {
    id: 'blog_how_to_connect',
    platform: 'blog',
    intent: INTENT_TYPES.HOW_TO_CONNECT,
    title: 'Connecter votre Blog',
    summary: 'Publiez automatiquement des articles sur votre blog.',
    steps: [
      {
        order: 1,
        title: 'Clé API ou Webhook',
        description: 'Votre blog doit supporter les publications via API ou webhook.',
        tip: 'WordPress, Ghost, Webflow, et autres CMS sont supportés'
      },
      {
        order: 2,
        title: 'Configurer l\'URL',
        description: 'Entrez l\'URL de votre endpoint de publication.',
        tip: 'Pour WordPress: votresite.com/wp-json/wp/v2/posts'
      },
      {
        order: 3,
        title: 'Clé d\'authentification',
        description: 'Entrez votre clé API ou token d\'accès.',
        tip: 'Ne partagez jamais votre clé API publiquement'
      }
    ],
    technical: {
      apiEndpoint: '/wp-json/wp/v2/posts',
      prerequisites: [
        'Blog avec accès API (WordPress, Ghost, Webflow...)',
        'Clé API ou mot de passe d\'application'
      ],
      docUrl: 'https://developer.wordpress.org/rest-api/reference/posts/'
    },
    ctas: [
      {
        label: 'Configurer Blog',
        action: CTA_ACTIONS.OPEN_PLATFORM_MODAL,
        payload: { platform: 'blog' },
        variant: 'primary'
      }
    ],
    troubleshooting: [
      {
        symptom: 'Erreur 401 ou 403',
        solution: 'Vérifiez que votre clé API est valide et a les permissions d\'écriture'
      }
    ],
    commonErrors: [
      {
        code: 'Erreur 401',
        meaning: 'Authentification échouée',
        fix: 'Vérifiez vos identifiants API'
      }
    ],
    // Difficulty information
    difficulty: {
      level: 3,
      label: 'Intermédiaire',
      whyThisDifficulty: [
        'Configuration dépend du CMS utilisé',
        'Génération de clé API requise',
        'Connaissances techniques recommandées'
      ]
    }
  },

  brevo: {
    id: 'brevo_how_to_connect',
    platform: 'brevo',
    intent: INTENT_TYPES.HOW_TO_CONNECT,
    title: 'Connecter Brevo (Email)',
    summary: 'Envoyez des campagnes email automatiques via Brevo.',
    steps: [
      {
        order: 1,
        title: 'Compte Brevo',
        description: 'Créez un compte gratuit sur Brevo (ex-Sendinblue).',
        tip: 'Le plan gratuit permet 300 emails/jour'
      },
      {
        order: 2,
        title: 'Obtenir la clé API',
        description: 'Dans Brevo: Paramètres → Clés API → Générer une clé.',
        tip: 'Copiez la clé v3, pas la clé v2'
      },
      {
        order: 3,
        title: 'Entrer la clé',
        description: 'Cliquez sur "Configurer" et collez votre clé API.',
        tip: 'La clé sera stockée de manière sécurisée'
      }
    ],
    technical: {
      apiKeyUrl: 'https://app.brevo.com/settings/keys/api',
      apiVersion: 'v3',
      prerequisites: [
        'Compte Brevo (ex-Sendinblue)',
        'Clé API v3 générée',
        'Expéditeur vérifié dans Brevo'
      ],
      docUrl: 'https://developers.brevo.com/docs'
    },
    ctas: [
      {
        label: 'Configurer Email',
        action: CTA_ACTIONS.OPEN_PLATFORM_MODAL,
        payload: { platform: 'brevo' },
        variant: 'primary'
      },
      {
        label: 'Obtenir clé API',
        action: CTA_ACTIONS.OPEN_EXTERNAL_LINK,
        payload: { url: 'https://app.brevo.com/settings/keys/api' },
        variant: 'secondary'
      },
      {
        label: 'Créer un compte Brevo',
        action: CTA_ACTIONS.OPEN_EXTERNAL_LINK,
        payload: { url: 'https://www.brevo.com/fr/' },
        variant: 'secondary'
      }
    ],
    troubleshooting: [
      {
        symptom: 'Clé API invalide',
        solution: 'Assurez-vous d\'utiliser une clé API v3, pas v2'
      },
      {
        symptom: 'Emails non envoyés',
        solution: 'Vérifiez que vous avez configuré et vérifié votre expéditeur dans Brevo'
      }
    ],
    commonErrors: [
      {
        code: 'Erreur 401',
        meaning: 'Clé API invalide',
        fix: 'Utilisez une clé API v3 (pas v2)'
      },
      {
        code: 'Emails non envoyés',
        meaning: 'Expéditeur non vérifié',
        fix: 'Configurez et vérifiez votre expéditeur dans Brevo'
      }
    ],
    // Difficulty information
    difficulty: {
      level: 3,
      label: 'Intermédiaire',
      whyThisDifficulty: [
        'Nécessite de créer un compte Brevo',
        'Générer une clé API v3',
        'Vérifier l\'adresse expéditeur'
      ]
    }
  },
  
  // NEW: WhatsApp manual playbook
  whatsapp: {
    id: 'whatsapp_how_to_use',
    platform: 'whatsapp',
    intent: INTENT_TYPES.HOW_TO_CONNECT,
    title: 'Utiliser WhatsApp (Mode Manuel)',
    summary: 'WhatsApp ne propose pas d\'API de publication. Utilisez le mode copier/coller.',
    steps: [
      {
        order: 1,
        title: 'Activez WhatsApp',
        description: 'Cliquez sur "Activer" pour ajouter WhatsApp à vos canaux.',
        tip: 'Aucune configuration OAuth n\'est nécessaire'
      },
      {
        order: 2,
        title: 'Générez votre contenu',
        description: 'L\'IA génère du contenu adapté aux stories WhatsApp.',
        tip: 'Le texte sera optimisé pour le format mobile'
      },
      {
        order: 3,
        title: 'Copiez et collez',
        description: 'Utilisez le bouton "Copier" dans le calendrier éditorial.',
        tip: 'Le texte + hashtags + lien CTA sont copiés automatiquement'
      },
      {
        order: 4,
        title: 'Publiez manuellement',
        description: 'Ouvrez WhatsApp et collez dans votre story ou groupe.',
        tip: 'Pour l\'image, elle s\'ouvre dans un nouvel onglet'
      }
    ],
    ctas: [
      {
        label: 'Activer WhatsApp',
        action: CTA_ACTIONS.OPEN_PLATFORM_MODAL,
        payload: { platform: 'whatsapp' },
        variant: 'primary'
      },
      {
        label: 'J\'ai compris',
        action: CTA_ACTIONS.DISMISS,
        variant: 'secondary'
      }
    ],
    tips: [
      'WhatsApp est idéal pour toucher une audience personnelle',
      'Les stories WhatsApp ont un taux d\'engagement très élevé',
      'Pas besoin de compte développeur - activation instantanée'
    ],
    troubleshooting: [],
    commonErrors: [],
    // Difficulty information
    difficulty: {
      level: 1,
      label: 'Très simple',
      whyThisDifficulty: [
        'Pas de connexion OAuth requise',
        'Pas de compte développeur à créer',
        'Activation en un clic'
      ]
    }
  },
  
  // NEW: Telegram playbook
  telegram: {
    id: 'telegram_how_to_connect',
    platform: 'telegram',
    intent: INTENT_TYPES.HOW_TO_CONNECT,
    title: 'Connecter Telegram',
    summary: 'Publiez automatiquement sur un canal Telegram via un bot.',
    steps: [
      {
        order: 1,
        title: 'Créer un bot',
        description: 'Ouvrez Telegram et cherchez @BotFather.',
        tip: 'Envoyez /newbot et suivez les instructions'
      },
      {
        order: 2,
        title: 'Copier le token',
        description: 'BotFather vous donnera un token d\'API.',
        tip: 'Gardez ce token secret - il donne accès à votre bot'
      },
      {
        order: 3,
        title: 'Créer ou choisir un canal',
        description: 'Créez un canal Telegram public ou privé.',
        tip: 'Notez le @username du canal (ex: @monchannel)'
      },
      {
        order: 4,
        title: 'Ajouter le bot comme admin',
        description: 'Ajoutez votre bot comme administrateur du canal.',
        tip: 'Le bot doit avoir les droits de publication'
      },
      {
        order: 5,
        title: 'Configurer',
        description: 'Cliquez sur "Configurer" et entrez le token et l\'ID du canal.',
        tip: 'L\'ID du canal peut être @username ou l\'ID numérique'
      }
    ],
    technical: {
      prerequisites: [
        'Compte Telegram',
        'Bot créé via @BotFather',
        'Canal où le bot est admin'
      ],
      docUrl: 'https://core.telegram.org/bots'
    },
    ctas: [
      {
        label: 'Configurer Telegram',
        action: CTA_ACTIONS.OPEN_PLATFORM_MODAL,
        payload: { platform: 'telegram' },
        variant: 'primary'
      },
      {
        label: 'Ouvrir BotFather',
        action: CTA_ACTIONS.OPEN_EXTERNAL_LINK,
        payload: { url: 'https://t.me/BotFather' },
        variant: 'secondary'
      }
    ],
    troubleshooting: [
      {
        symptom: 'Bot ne peut pas publier',
        solution: 'Vérifiez que le bot est admin du canal avec droits de publication'
      },
      {
        symptom: 'Channel ID invalide',
        solution: 'Utilisez @username pour les canaux publics, ou l\'ID numérique pour les privés'
      }
    ],
    commonErrors: [
      {
        code: 'Forbidden',
        meaning: 'Bot n\'est pas admin du canal',
        fix: 'Ajoutez le bot comme administrateur'
      },
      {
        code: 'Chat not found',
        meaning: 'ID de canal incorrect',
        fix: 'Vérifiez le @username ou l\'ID numérique'
      }
    ],
    // Difficulty information
    difficulty: {
      level: 2,
      label: 'Simple',
      whyThisDifficulty: [
        'Création de bot via @BotFather (gratuit)',
        'Pas de compte développeur externe',
        'Configuration en quelques minutes'
      ]
    }
  }
};

// =============================================================================
// PLAYBOOKS: GETTING STARTED BY STEP
// =============================================================================
const STEP_PLAYBOOKS = {
  analyze: {
    id: 'analyze_getting_started',
    intent: INTENT_TYPES.GETTING_STARTED,
    title: 'Analyser votre site',
    summary: 'L\'IA analyse votre site web pour extraire les thèmes marketing de votre marque.',
    steps: [
      {
        order: 1,
        title: 'Entrez l\'URL',
        description: 'Entrez l\'adresse complète de votre site (ex: https://exemple.com).'
      },
      {
        order: 2,
        title: 'Lancez l\'analyse',
        description: 'Cliquez sur "Analyser" et patientez pendant l\'extraction.'
      },
      {
        order: 3,
        title: 'Validez les thèmes',
        description: 'Vérifiez les thèmes détectés et validez-les pour continuer.'
      }
    ],
    ctas: [
      {
        label: 'J\'ai compris',
        action: CTA_ACTIONS.DISMISS,
        variant: 'secondary'
      }
    ],
    tips: [
      'L\'analyse fonctionne mieux avec des sites riches en contenu',
      'Vous pouvez modifier les thèmes manuellement après l\'analyse'
    ]
  },

  connect: {
    id: 'connect_getting_started',
    intent: INTENT_TYPES.GETTING_STARTED,
    title: 'Connecter vos comptes',
    summary: 'Reliez vos réseaux sociaux pour publier automatiquement.',
    steps: [
      {
        order: 1,
        title: 'Choisissez une plateforme',
        description: 'Cliquez sur "Configurer" à côté de la plateforme souhaitée.'
      },
      {
        order: 2,
        title: 'Autorisez l\'accès',
        description: 'Suivez les instructions pour autoriser notre application.'
      },
      {
        order: 3,
        title: 'Répétez si besoin',
        description: 'Connectez autant de plateformes que souhaité.'
      }
    ],
    ctas: [
      {
        label: 'Voir les plateformes',
        action: CTA_ACTIONS.GO_TO_STEP,
        payload: { step: 'connect' },
        variant: 'primary'
      }
    ],
    tips: [
      'Commencez par votre canal principal (Instagram, Email, etc.)',
      'Vous pouvez toujours ajouter d\'autres plateformes plus tard'
    ]
  },

  campaign: {
    id: 'campaign_getting_started',
    intent: INTENT_TYPES.GETTING_STARTED,
    title: 'Créer votre campagne',
    summary: 'Définissez vos objectifs et la fréquence de publication.',
    steps: [
      {
        order: 1,
        title: 'Objectif marketing',
        description: 'Choisissez votre objectif : Visibilité, Trafic, Conversion, etc.'
      },
      {
        order: 2,
        title: 'Fréquence',
        description: 'Définissez à quelle fréquence publier (1/jour recommandé).'
      },
      {
        order: 3,
        title: 'Plateformes',
        description: 'Sélectionnez les plateformes connectées pour cette campagne.'
      }
    ],
    ctas: [
      {
        label: 'Créer la campagne',
        action: CTA_ACTIONS.GO_TO_STEP,
        payload: { step: 'campaign' },
        variant: 'primary'
      }
    ],
    tips: [
      'Un objectif clair améliore la qualité du contenu généré',
      '1 publication par jour est un bon point de départ'
    ]
  },

  generate: {
    id: 'generate_getting_started',
    intent: INTENT_TYPES.GETTING_STARTED,
    title: 'Générer votre contenu',
    summary: 'L\'IA génère 30 jours de contenu personnalisé.',
    steps: [
      {
        order: 1,
        title: 'Lancez la génération',
        description: 'Cliquez sur "Générer" pour créer automatiquement vos publications.'
      },
      {
        order: 2,
        title: 'Patientez',
        description: 'La génération peut prendre quelques minutes.'
      },
      {
        order: 3,
        title: 'Vérifiez',
        description: 'Consultez et modifiez le contenu généré si nécessaire.'
      }
    ],
    ctas: [
      {
        label: 'Lancer la génération',
        action: CTA_ACTIONS.GO_TO_STEP,
        payload: { step: 'generate' },
        variant: 'primary'
      }
    ],
    tips: [
      'Le contenu est adapté à chaque plateforme automatiquement',
      'Vous pouvez modifier chaque publication avant envoi'
    ]
  },

  results: {
    id: 'results_getting_started',
    intent: INTENT_TYPES.GETTING_STARTED,
    title: 'Vos résultats',
    summary: 'Consultez vos publications et analysez les performances.',
    steps: [
      {
        order: 1,
        title: 'Vue d\'ensemble',
        description: 'Visualisez toutes vos publications planifiées.'
      },
      {
        order: 2,
        title: 'Métriques',
        description: 'Suivez l\'engagement et la portée de vos publications.'
      }
    ],
    ctas: [
      {
        label: 'Voir les résultats',
        action: CTA_ACTIONS.GO_TO_STEP,
        payload: { step: 'results' },
        variant: 'primary'
      }
    ]
  }
};

// =============================================================================
// PLAYBOOKS: ERROR HELP
// =============================================================================
const ERROR_PLAYBOOKS = {
  no_platforms_connected: {
    id: 'error_no_platforms',
    intent: INTENT_TYPES.ERROR_HELP,
    title: 'Aucune plateforme connectée',
    summary: 'Vous devez connecter au moins une plateforme pour créer une campagne.',
    steps: [
      {
        order: 1,
        title: 'Retournez à l\'étape Connexion',
        description: 'Cliquez sur "Connecter mes comptes" dans le menu.'
      },
      {
        order: 2,
        title: 'Connectez une plateforme',
        description: 'Choisissez et configurez au moins une plateforme.'
      }
    ],
    ctas: [
      {
        label: 'Aller à Connexion',
        action: CTA_ACTIONS.GO_TO_STEP,
        payload: { step: 'connect' },
        variant: 'primary'
      }
    ]
  },

  themes_not_validated: {
    id: 'error_themes_not_validated',
    intent: INTENT_TYPES.ERROR_HELP,
    title: 'Thèmes non validés',
    summary: 'Vous devez valider vos thèmes marketing avant de générer du contenu.',
    steps: [
      {
        order: 1,
        title: 'Retournez à l\'analyse',
        description: 'Cliquez sur "Analyser mon site" dans le menu.'
      },
      {
        order: 2,
        title: 'Validez les thèmes',
        description: 'Vérifiez les thèmes et cliquez sur "Valider".'
      }
    ],
    ctas: [
      {
        label: 'Aller à l\'analyse',
        action: CTA_ACTIONS.GO_TO_STEP,
        payload: { step: 'analyze' },
        variant: 'primary'
      }
    ]
  },

  campaign_not_configured: {
    id: 'error_campaign_not_configured',
    intent: INTENT_TYPES.ERROR_HELP,
    title: 'Campagne non configurée',
    summary: 'Vous devez configurer une campagne avant de générer du contenu.',
    steps: [
      {
        order: 1,
        title: 'Créez une campagne',
        description: 'Définissez votre objectif et fréquence de publication.'
      }
    ],
    ctas: [
      {
        label: 'Créer la campagne',
        action: CTA_ACTIONS.GO_TO_STEP,
        payload: { step: 'campaign' },
        variant: 'primary'
      }
    ]
  },

  reauth_required: {
    id: 'error_reauth_required',
    intent: INTENT_TYPES.ERROR_HELP,
    title: 'Reconnexion requise',
    summary: 'Votre connexion à cette plateforme a expiré ou été révoquée.',
    steps: [
      {
        order: 1,
        title: 'Reconnectez-vous',
        description: 'Cliquez sur "Reconnecter" pour rétablir l\'accès.'
      }
    ],
    ctas: [
      {
        label: 'Reconnecter',
        action: CTA_ACTIONS.GO_TO_STEP,
        payload: { step: 'connect' },
        variant: 'primary'
      }
    ]
  }
};

// =============================================================================
// INTENT ROUTER - Determines which playbook to show
// =============================================================================

/**
 * Route to the best playbook based on context
 * Priority order:
 * 1. User's explicit message/question
 * 2. Last error or action failure
 * 3. Current step needs
 * 4. Platform-specific help if focused
 * 5. Default step guidance
 */
export function routeToPlaybook(context) {
  const {
    currentStep,
    selectedPlatform,
    channelsStatus = {},
    lastUserAction,
    lastError,
    userMessage,
    campaignDraft,
    projectStatus = {}
  } = context;

  // Priority 1: Error state
  if (lastError) {
    const errorPlaybook = getErrorPlaybook(lastError);
    if (errorPlaybook) return errorPlaybook;
  }

  // Priority 2: Platform focus (user clicked "Configurer" or similar)
  if (selectedPlatform && lastUserAction?.type === 'CLICK_CONFIGURE_PLATFORM') {
    return CONNECT_PLAYBOOKS[selectedPlatform] || null;
  }

  // Priority 3: Step-specific issues
  const connectedCount = Object.values(channelsStatus).filter(c => c?.connected).length;
  
  if (currentStep === 'campaign' && connectedCount === 0) {
    return ERROR_PLAYBOOKS.no_platforms_connected;
  }

  if (currentStep === 'generate' && !projectStatus?.steps?.themes_validated) {
    return ERROR_PLAYBOOKS.themes_not_validated;
  }

  if (currentStep === 'generate' && !projectStatus?.steps?.campaign_configured) {
    return ERROR_PLAYBOOKS.campaign_not_configured;
  }

  // Priority 4: User asked about specific platform
  if (userMessage) {
    const platformMention = detectPlatformMention(userMessage);
    if (platformMention) {
      return CONNECT_PLAYBOOKS[platformMention] || null;
    }
  }

  // Priority 5: Step getting started guide
  if (currentStep && STEP_PLAYBOOKS[currentStep]) {
    return STEP_PLAYBOOKS[currentStep];
  }

  // Default: null (show general assistant)
  return null;
}

/**
 * Get error-specific playbook
 */
function getErrorPlaybook(error) {
  const errorCode = error?.error_code || error?.code || error?.type;
  
  if (!errorCode) return null;

  const errorMap = {
    'no_platforms_connected': ERROR_PLAYBOOKS.no_platforms_connected,
    'NO_PLATFORMS_CONNECTED': ERROR_PLAYBOOKS.no_platforms_connected,
    'themes_not_validated': ERROR_PLAYBOOKS.themes_not_validated,
    'THEMES_NOT_VALIDATED': ERROR_PLAYBOOKS.themes_not_validated,
    'campaign_not_configured': ERROR_PLAYBOOKS.campaign_not_configured,
    'CAMPAIGN_NOT_CONFIGURED': ERROR_PLAYBOOKS.campaign_not_configured,
    'reauth_required': ERROR_PLAYBOOKS.reauth_required,
    'REAUTH_REQUIRED': ERROR_PLAYBOOKS.reauth_required
  };

  return errorMap[errorCode] || null;
}

/**
 * Detect if user mentions a specific platform
 */
function detectPlatformMention(message) {
  if (!message) return null;
  
  const lower = message.toLowerCase();
  
  const platformKeywords = {
    instagram: ['instagram', 'insta', 'ig'],
    facebook: ['facebook', 'fb'],
    x: ['twitter', 'x', 'tweet'],
    linkedin: ['linkedin', 'li'],
    blog: ['blog', 'wordpress', 'ghost', 'webflow', 'article'],
    brevo: ['email', 'mail', 'brevo', 'sendinblue', 'newsletter']
  };

  for (const [platform, keywords] of Object.entries(platformKeywords)) {
    if (keywords.some(kw => lower.includes(kw))) {
      return platform;
    }
  }

  return null;
}

// =============================================================================
// EXPORTS
// =============================================================================
export const helpPlaybooks = {
  connect: CONNECT_PLAYBOOKS,
  steps: STEP_PLAYBOOKS,
  errors: ERROR_PLAYBOOKS
};

export function getConnectPlaybook(platformId) {
  return CONNECT_PLAYBOOKS[platformId] || null;
}

export function getStepPlaybook(stepId) {
  return STEP_PLAYBOOKS[stepId] || null;
}

export function getErrorPlaybookByCode(errorCode) {
  return getErrorPlaybook({ error_code: errorCode });
}

// Get all available platforms for connect
export function getAvailablePlatforms() {
  return PLATFORM_IDS.map(id => ({
    id,
    name: getPlatformName(id),
    playbook: CONNECT_PLAYBOOKS[id]
  }));
}
